import { sectionRenderer } from '@theme/section-renderer';
import { announce, createFocusTrap, focusFirstTabbable, safeFocus } from '@nulo/a11y';
import { sendCollectionView } from './nulo-gtm.js';

/**
 * @param {string} value
 * @returns {string}
 */
function handleize(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * @param {string} label
 * @returns {string}
 */
function normalizeFilterTitle(label) {
  return handleize(String(label || '').split('_')[0]);
}

/**
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 */
function matchesFilterTitle(a, b) {
  if (!a || !b) return false;

  return (
    normalizeFilterTitle(a) === normalizeFilterTitle(b) || handleize(a) === handleize(b)
  );
}

/**
 * @param {string} tagName
 * @param {string} label
 * @param {string} [paramValue]
 * @returns {boolean}
 */
function matchesStaticTag(tagName, label, paramValue = '') {
  if (!tagName) return false;

  const tag = handleize(tagName);
  const candidates = new Set(
    [label, paramValue]
      .filter(Boolean)
      .flatMap((value) => {
        const stringValue = String(value);
        const segments = stringValue.split('_');
        const lastSegment = segments[segments.length - 1];
        return [handleize(stringValue), handleize(lastSegment)];
      })
  );

  return [...candidates].some((candidate) => candidate === tag);
}

const COLLECTION_DEBUG_PARAM = 'nulo_debug';
const COLLECTION_DEBUG_VALUE = 'collection';

/**
 * @returns {boolean}
 */
function isCollectionDebugMode() {
  return (
    new URL(window.location.href).searchParams.get(COLLECTION_DEBUG_PARAM) ===
    COLLECTION_DEBUG_VALUE
  );
}

/**
 * @param {URL} url
 */
function preserveCollectionDebugParam(url) {
  const debugParam = new URL(window.location.href).searchParams.get(COLLECTION_DEBUG_PARAM);
  if (debugParam) {
    url.searchParams.set(COLLECTION_DEBUG_PARAM, debugParam);
  }
}

/**
 * @param {{filterTitle?: string, tagName?: string} | null | undefined} activeStaticFilter
 * @param {string} filterLabel
 * @param {string} optionLabel
 * @param {string} optionValue
 * @returns {boolean}
 */
function isOptionChecked(activeStaticFilter, filterLabel, optionLabel, optionValue) {
  if (!activeStaticFilter?.tagName) return false;
  if (!matchesFilterTitle(activeStaticFilter.filterTitle || '', filterLabel)) return false;
  return matchesStaticTag(activeStaticFilter.tagName, optionLabel, optionValue);
}

/**
 * Hide placeholder metafield values like "N/A" from collection filters.
 * @param {string} [label]
 * @param {string} [value]
 * @returns {boolean}
 */
function isHiddenFilterOption(label, value) {
  const normalize = (input) => String(input || '').trim().toLowerCase();
  const normalizedLabel = normalize(label);
  const normalizedValue = normalize(value);

  return (
    normalizedLabel === 'n/a' ||
    normalizedLabel === 'na' ||
    normalizedValue === 'n/a' ||
    normalizedValue === 'na'
  );
}

/**
 * @param {URL} url
 * @returns {URL}
 */
function toBrowserUrl(url) {
  const browserUrl = new URL(url.href);
  browserUrl.searchParams.delete('section_id');
  return browserUrl;
}

/**
 * @extends {HTMLElement}
 */
class CollectionMain extends HTMLElement {
  /** @type {AbortController | null} */
  _abort = null;

  /** @type {(() => void) | null} */
  _filterTrap = null;

  /** @type {(() => void) | null} */
  _sortTrap = null;

  /** @type {boolean} */
  _initialized = false;

  /** @type {boolean} */
  _isRendering = false;

  /** @type {number} */
  _idleId = 0;

  /** @type {number} */
  _idleTimer = 0;

  /** @type {number} */
  _hydrateGeneration = 0;

  /** @type {boolean} */
  _catalogHydrating = false;

  connectedCallback() {
    if (this._initialized) return;

    if (this.#recoverFromSectionOnlyUrl()) return;

    this._initialized = true;

    this.#bindEvents();
    this.#syncSortState();
    this.#updateHeaderOffset();
    this.#scheduleIdleWork();
  }

  #parseFiltersContext() {
    const el = this.querySelector('[data-filters-context]');
    if (!el?.textContent) return null;

    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  /**
   * @param {Array<{label: string, paramName: string, values: Array<{label: string, value: string, paramName: string}>}>} parentCatalog
   * @param {Array<{paramName: string, values: Array<{value: string, count: number, active: boolean, paramName: string}>}>} childContext
   * @param {ReturnType<CollectionMain['#parseConfig']>} config
   */
  #mergeParentCatalogIntoFilters(parentCatalog, childContext, config) {
    const unavailableLabel = config?.i18n?.filterOptionUnavailable || '';

    for (const panel of this.querySelectorAll('[data-collection-filters-panel]')) {
      for (const catalogGroup of parentCatalog) {
        const details = panel.querySelector(
          `[data-filter-param="${CSS.escape(catalogGroup.paramName)}"]`
        );
        if (!(details instanceof HTMLDetailsElement)) continue;

        const list = details.querySelector('.collection-filters__list');
        if (!(list instanceof HTMLUListElement)) continue;

        const existingValues = new Set(
          [...list.querySelectorAll('[data-filter-param-value]')].map((input) =>
            input.getAttribute('data-filter-param-value')
          )
        );

        for (const option of catalogGroup.values || []) {
          if (isHiddenFilterOption(option.label, option.value)) continue;
          if (existingValues.has(option.value)) continue;

          const contextOption = this.#lookupContextOption(
            childContext,
            catalogGroup.paramName,
            option.value
          );
          const checked =
            contextOption.active ||
            isOptionChecked(config?.activeStaticFilter, catalogGroup.label, option.label, option.value);
          const disabled = contextOption.count === 0 && !checked;

          list.appendChild(
            this.#createFilterOptionElement({
              filterLabel: catalogGroup.label,
              optionLabel: option.label,
              optionValue: option.value,
              paramName: option.paramName || contextOption.paramName || catalogGroup.paramName,
              checked,
              disabled,
              unavailableLabel,
            })
          );
        }

        details.setAttribute(
          'data-filter-options-count',
          String(list.querySelectorAll('.collection-filters__item').length)
        );
      }
    }
  }

  /**
   * @param {Document} parentDoc
   * @param {Array<{paramName: string, values: Array<{value: string, count: number, active: boolean, paramName: string}>}>} childContext
   * @param {ReturnType<CollectionMain['#parseConfig']>} config
   */
  #appendMissingParentFilterGroups(parentDoc, childContext, config) {
    const parentForm = parentDoc.querySelector('[data-collection-filters-form]');
    if (!(parentForm instanceof HTMLFormElement)) return;

    for (const panel of this.querySelectorAll('[data-collection-filters-panel]')) {
      const form = panel.querySelector('[data-collection-filters-form]');
      if (!(form instanceof HTMLFormElement)) continue;

      for (const parentDetails of parentForm.querySelectorAll('.collection-filters__group')) {
        const param = parentDetails.getAttribute('data-filter-param');
        if (!param || form.querySelector(`[data-filter-param="${CSS.escape(param)}"]`)) continue;

        const clone = parentDetails.cloneNode(true);
        if (clone instanceof HTMLDetailsElement) {
          this.#removeHiddenFilterOptions(clone);
          if (!clone.querySelector('.collection-filters__item')) continue;

          this.#syncFilterGroupToChildContext(clone, childContext, config);
          form.appendChild(clone);
        }
      }
    }
  }

  /**
   * Remove placeholder "N/A" options from a filter group.
   * @param {HTMLDetailsElement} details
   */
  #removeHiddenFilterOptions(details) {
    for (const input of details.querySelectorAll('[data-collection-filter]')) {
      if (!(input instanceof HTMLInputElement)) continue;

      const optionLabel = input.dataset.filterValue || '';
      const optionValue = input.getAttribute('data-filter-param-value') || input.value;
      if (!isHiddenFilterOption(optionLabel, optionValue)) continue;

      input.closest('.collection-filters__item')?.remove();
    }

    const list = details.querySelector('.collection-filters__list');
    if (list) {
      details.setAttribute(
        'data-filter-options-count',
        String(list.querySelectorAll('.collection-filters__item').length)
      );
    }
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {Array<{paramName: string, label: string, values: Array<{value: string, count: number, active: boolean, paramName: string, label: string}>}>} childContext
   * @param {ReturnType<CollectionMain['#parseConfig']>} config
   */
  #syncFilterGroupToChildContext(details, childContext, config) {
    const paramName = details.getAttribute('data-filter-param') || '';
    const filterLabel =
      details.querySelector('.collection-filters__group-label')?.textContent?.trim() || '';
    const unavailableLabel = config?.i18n?.filterOptionUnavailable || '';

    for (const input of details.querySelectorAll('[data-collection-filter]')) {
      if (!(input instanceof HTMLInputElement)) continue;

      const optionValue = input.getAttribute('data-filter-param-value') || input.value;
      const optionLabel = input.dataset.filterValue || optionValue;
      const contextOption = this.#lookupContextOption(childContext, paramName, optionValue);
      const checked =
        contextOption.active ||
        isOptionChecked(config?.activeStaticFilter, filterLabel, optionLabel, optionValue);
      const disabled = contextOption.count === 0 && !checked;

      input.checked = checked;
      input.disabled = disabled;
      if (disabled) {
        input.setAttribute('aria-disabled', 'true');
      } else {
        input.removeAttribute('aria-disabled');
      }

      const item = input.closest('.collection-filters__item');
      const label = input.closest('.collection-filters__label');
      item?.classList.toggle('collection-filters__item--disabled', disabled);
      label?.classList.toggle('collection-filters__label--disabled', disabled);

      if (disabled && label && !label.querySelector('.visually-hidden')) {
        const sr = document.createElement('span');
        sr.className = 'visually-hidden';
        sr.textContent = unavailableLabel;
        label.querySelector('.collection-filters__text')?.appendChild(sr);
      }
    }

    const list = details.querySelector('.collection-filters__list');
    if (list) {
      details.setAttribute(
        'data-filter-options-count',
        String(list.querySelectorAll('.collection-filters__item').length)
      );
    }
  }

  /**
   * @param {Array<{paramName: string, values: Array<{value: string, count: number, active: boolean, paramName: string}>}>} childContext
   * @param {string} paramName
   * @param {string} value
   */
  #lookupContextOption(childContext, paramName, value) {
    const group = childContext.find((item) => item.paramName === paramName);
    if (!group?.values?.length) {
      return { count: 0, active: false, paramName };
    }

    const match = group.values.find((item) => item.value === value);
    if (!match) {
      return { count: 0, active: false, paramName };
    }

    return {
      count: match.count ?? 0,
      active: Boolean(match.active),
      paramName: match.paramName || paramName,
    };
  }

  /**
   * @param {{
   *   filterLabel: string,
   *   optionLabel: string,
   *   optionValue: string,
   *   paramName: string,
   *   checked: boolean,
   *   disabled: boolean,
   *   unavailableLabel: string,
   * }} options
   * @returns {HTMLLIElement}
   */
  #createFilterOptionElement(options) {
    const li = document.createElement('li');
    li.className = `collection-filters__item${options.disabled ? ' collection-filters__item--disabled' : ''}`;

    const label = document.createElement('label');
    label.className = `collection-filters__label${options.disabled ? ' collection-filters__label--disabled' : ''}`;

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.className = 'collection-filters__input visually-hidden';
    input.name = options.paramName;
    input.value = options.optionValue;
    input.dataset.collectionFilter = '';
    input.dataset.filterLabel = options.filterLabel;
    input.dataset.filterValue = options.optionLabel;
    input.dataset.filterParamValue = options.optionValue;
    input.dataset.isAmbassador = 'false';
    if (options.checked) input.checked = true;
    if (options.disabled) {
      input.disabled = true;
      input.setAttribute('aria-disabled', 'true');
    }

    const checkmark = document.createElement('span');
    checkmark.className = 'collection-filters__checkmark';
    checkmark.setAttribute('aria-hidden', 'true');

    const text = document.createElement('span');
    text.className = 'collection-filters__text';
    text.textContent = options.optionLabel;
    if (options.disabled && options.unavailableLabel) {
      const sr = document.createElement('span');
      sr.className = 'visually-hidden';
      sr.textContent = options.unavailableLabel;
      text.appendChild(sr);
    }

    label.append(input, checkmark, text);
    li.append(label);
    return li;
  }

  async #hydrateParentFiltersCatalog() {
    const config = this.#parseConfig();
    if (!config?.isStaticChildPlp || !config.catalogCollectionUrl || !config.sectionId) return;
    if (this._catalogHydrating || this.hasAttribute('data-catalog-hydrated')) return;

    const childContext = this.#parseFiltersContext();
    if (!childContext?.length) return;

    this._catalogHydrating = true;
    this._hydrateGeneration += 1;
    const generation = this._hydrateGeneration;

    try {
      const parentUrl = new URL(config.catalogCollectionUrl, window.location.origin);
      const html = await sectionRenderer.getSectionHTML(String(config.sectionId), false, parentUrl);
      if (generation !== this._hydrateGeneration) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const catalogEl = doc.querySelector('[data-filters-taxonomy]');
      if (!catalogEl?.textContent) return;

      const parentCatalog = JSON.parse(catalogEl.textContent);
      this.#appendMissingParentFilterGroups(doc, childContext, config);
      this.#mergeParentCatalogIntoFilters(parentCatalog, childContext, config);
      this.setAttribute('data-catalog-hydrated', 'true');

      for (const panel of this.querySelectorAll('[data-collection-filters-panel]')) {
        panel.removeAttribute('data-collection-filters-awaiting-catalog');
      }

      if (isCollectionDebugMode()) {
        this.#logDebugConfig();
      }
    } catch (error) {
      console.warn('[collection-main] Parent filter catalog hydration failed', error);

      for (const panel of this.querySelectorAll('[data-collection-filters-panel]')) {
        panel.removeAttribute('data-collection-filters-awaiting-catalog');
      }
    } finally {
      if (generation === this._hydrateGeneration) {
        this._catalogHydrating = false;
      }
    }
  }

  #parseDebugConfig() {
    const el = this.querySelector('[data-collection-debug]');
    if (!el?.textContent) return null;

    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  #initCollectionDebug() {
    if (!isCollectionDebugMode()) return;

    const panel = this.querySelector('[data-collection-debug-panel]');
    if (panel instanceof HTMLElement) {
      panel.hidden = false;
    }

    this.#logDebugConfig();
  }

  #logDebugConfig() {
    if (!isCollectionDebugMode()) return;

    const config = this.#parseConfig();
    const debug = this.#parseDebugConfig();

    if (!config) {
      console.warn('[collection-main] Debug mode is on but data-config JSON failed to parse.');
      return;
    }

    if (!debug?.debugSchemaVersion) {
      console.warn(
        '[collection-main] OLD theme build detected — debug JSON is missing debugSchemaVersion. Push latest theme files (collection-main.liquid, collection-filters.liquid, collection-filter-option.liquid).'
      );
    } else if (debug.debugSchemaVersion < 5) {
      console.warn(
        '[collection-main] Stale theme build — debugSchemaVersion is',
        debug.debugSchemaVersion,
        'but current is 5. Re-push theme.'
      );
    }

    const filtersBuild = this.querySelector('[data-collection-filters-build]');
    if (!(filtersBuild instanceof HTMLElement)) {
      console.warn('[collection-main] Missing data-collection-filters-build — collection-filters.liquid not deployed.');
    }

    console.info('[collection-main] static collection debug', {
      debug,
      staticCollections: config.staticCollections,
      activeStaticFilter: config.activeStaticFilter,
      parentCollectionUrl: config.parentCollectionUrl,
    });

    const sidebarGroups = this.querySelectorAll('.collection-filters--desktop [data-filter-options-count]');
    const sidebarSummary = [...sidebarGroups].map((group) => {
      const expected = Number(group.getAttribute('data-filter-options-count') || 0);
      const rendered = group.querySelectorAll('.collection-filters__item').length;
      return {
        param: group.getAttribute('data-filter-param'),
        expected,
        rendered,
        match: expected === rendered,
      };
    });

    console.info('[collection-main] sidebar option counts (desktop)', sidebarSummary);
  }

  /**
   * If the page was refreshed with section_id in the URL, Shopify returns only
   * that section (no header/footer). Redirect to the clean collection URL.
   * @returns {boolean}
   */
  #recoverFromSectionOnlyUrl() {
    const pageUrl = new URL(window.location.href);
    if (!pageUrl.searchParams.has('section_id')) return false;

    pageUrl.searchParams.delete('section_id');
    window.location.replace(pageUrl.toString());
    return true;
  }

  disconnectedCallback() {
    this.#cancelIdleWork();
    this._abort?.abort();
    this._abort = null;
    this._filterTrap?.();
    this._sortTrap?.();
    this._filterTrap = null;
    this._sortTrap = null;
    this._catalogHydrating = false;
    this._hydrateGeneration += 1;
    this._initialized = false;
  }

  #scheduleIdleWork() {
    this.#cancelIdleWork();

    const run = () => {
      this._idleId = 0;
      this._idleTimer = 0;
      if (!this.isConnected) return;
      this.#sendGtmView();
      this.#initCollectionDebug();
      this.#hydrateParentFiltersCatalog();
    };

    if (typeof window.requestIdleCallback === 'function') {
      this._idleId = window.requestIdleCallback(run, { timeout: 2500 });
      return;
    }

    this._idleTimer = window.setTimeout(run, 1);
  }

  #cancelIdleWork() {
    if (this._idleId) {
      window.cancelIdleCallback?.(this._idleId);
      this._idleId = 0;
    }

    if (this._idleTimer) {
      window.clearTimeout(this._idleTimer);
      this._idleTimer = 0;
    }
  }

  #parseConfig() {
    const el = this.querySelector('[data-config]');
    if (!el?.textContent) return null;

    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  #parseGtmProducts() {
    const el = this.querySelector('[data-gtm-products]');
    if (!el?.textContent) return [];

    try {
      return JSON.parse(el.textContent);
    } catch {
      return [];
    }
  }

  #bindEvents() {
    this._abort?.abort();
    this._abort = new AbortController();
    const { signal } = this._abort;

    this.addEventListener(
      'change',
      (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;
        if (!input.matches('[data-collection-filter]')) return;
        this.#onFilterChange(input);
      },
      { signal }
    );

    for (const select of this.querySelectorAll('[data-collection-sort]')) {
      select.addEventListener(
        'change',
        () => {
          if (!(select instanceof HTMLSelectElement)) return;
          this.#applySort(select.value);
        },
        { signal }
      );
    }

    this.addEventListener(
      'click',
      (event) => {
        const pageLink = event.target.closest('[data-collection-page]');
        if (pageLink instanceof HTMLAnchorElement) {
          event.preventDefault();
          this.#renderFromUrl(new URL(pageLink.href), { focusTarget: '[data-collection-grid]' });
          return;
        }

        const clearLink = event.target.closest('[data-collection-clear]');
        if (clearLink instanceof HTMLAnchorElement) {
          event.preventDefault();
          this.#renderFromUrl(new URL(clearLink.href), { focusTarget: '[data-collection-filters-form]' });
          return;
        }

        const sortOption = event.target.closest('[data-collection-sort-option]');
        if (sortOption instanceof HTMLButtonElement) {
          this.#applySort(sortOption.getAttribute('value') || 'manual');
          this.#closeSortDrawer();
        }
      },
      { signal }
    );

    this.querySelector('[data-collection-filter-open]')?.addEventListener(
      'click',
      () => this.#openFilterDrawer(),
      { signal }
    );

    this.querySelector('[data-collection-filter-close]')?.addEventListener(
      'click',
      () => this.#closeFilterDrawer(),
      { signal }
    );

    this.querySelector('[data-collection-filter-apply]')?.addEventListener(
      'click',
      () => this.#closeFilterDrawer(),
      { signal }
    );

    this.querySelector('[data-collection-sort-open]')?.addEventListener(
      'click',
      () => this.#openSortDrawer(),
      { signal }
    );

    this.querySelector('[data-collection-sort-close]')?.addEventListener(
      'click',
      () => this.#closeSortDrawer(),
      { signal }
    );

    for (const drawer of this.querySelectorAll('dialog.collection-main__drawer')) {
      drawer.addEventListener(
        'cancel',
        (event) => {
          event.preventDefault();
          if (drawer.hasAttribute('data-collection-filter-drawer')) {
            this.#closeFilterDrawer();
          } else {
            this.#closeSortDrawer();
          }
        },
        { signal }
      );

      drawer.addEventListener(
        'click',
        (event) => {
          if (event.target === drawer) {
            if (drawer.hasAttribute('data-collection-filter-drawer')) {
              this.#closeFilterDrawer();
            } else {
              this.#closeSortDrawer();
            }
          }
        },
        { signal }
      );
    }

    window.addEventListener(
      'popstate',
      () => {
        this.#renderFromUrl(new URL(window.location.href), { skipPush: true });
      },
      { signal }
    );

    window.addEventListener(
      'resize',
      () => {
        this.#updateHeaderOffset();
        this.#syncFilterTreeHost();
      },
      { signal }
    );
  }

  #updateHeaderOffset() {
    const header = document.querySelector('.primary-header, header-component, .header');
    if (!(header instanceof HTMLElement)) return;
    document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
  }

  /**
   * @param {HTMLInputElement} input
   */
  #onFilterChange(input) {
    const config = this.#parseConfig();

    if (!input.checked && config?.activeStaticFilter && config.parentCollectionUrl) {
      const isActiveStaticFilter =
        matchesFilterTitle(input.dataset.filterLabel, config.activeStaticFilter.filterTitle) &&
        matchesStaticTag(
          config.activeStaticFilter.tagName,
          input.dataset.filterValue,
          input.dataset.filterParamValue
        );

      if (isActiveStaticFilter) {
        window.location.assign(config.parentCollectionUrl);
        return;
      }
    }

    const redirectUrl = this.#getStaticCollectionRedirect(input);
    if (redirectUrl) {
      window.location.assign(redirectUrl);
      return;
    }

    const form = input.closest('[data-collection-filters-form]');
    const url = this.#buildUrlFromFilters(form instanceof HTMLFormElement ? form : undefined);
    this.#renderFromUrl(url, { focusTarget: '[data-collection-grid]' });
  }

  /**
   * @param {HTMLInputElement} changedInput
   * @returns {string | null}
   */
  #getStaticCollectionRedirect(changedInput) {
    if (!changedInput.checked) return null;

    const config = this.#parseConfig();
    if (!config) return null;

    const form = changedInput.closest('[data-collection-filters-form]');
    const selected = this.#getSelectedFilters(
      form instanceof HTMLFormElement ? form : undefined
    );
    if (selected.length !== 1) return null;

    const selectedFilter = selected[0];

    if (selectedFilter.isAmbassador) {
      const rootUrl = config.rootUrl || '/';
      return `${rootUrl}collections/ambassador-${handleize(selectedFilter.value)}`;
    }

    const match = (config.staticCollections || []).find((entry) => {
      return (
        matchesFilterTitle(entry.filterTitle, selectedFilter.label) &&
        matchesStaticTag(entry.tagName, selectedFilter.value, selectedFilter.paramValue)
      );
    });

    return match?.url || null;
  }

  /**
   * @returns {Array<{ label: string, value: string, paramValue: string, isAmbassador: boolean }>}
   */
  #getSelectedFilters(sourceForm) {
    const selected = [];
    const form = sourceForm || this.#getActiveFilterForm();
    if (!(form instanceof HTMLFormElement)) return selected;

    for (const input of form.querySelectorAll('[data-collection-filter]:checked')) {
      if (!(input instanceof HTMLInputElement)) continue;
      selected.push({
        label: input.dataset.filterLabel || '',
        value: input.dataset.filterValue || '',
        paramValue: input.dataset.filterParamValue || input.value || '',
        isAmbassador: input.dataset.isAmbassador === 'true',
      });
    }

    return selected;
  }

  /**
   * @param {HTMLFormElement} [sourceForm]
   * @returns {URL}
   */
  #buildUrlFromFilters(sourceForm) {
    const config = this.#parseConfig();
    const baseUrl = config?.collectionUrl || window.location.pathname;
    const url = new URL(baseUrl, window.location.origin);

    preserveCollectionDebugParam(url);

    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('filter.') || key === 'sort_by' || key === 'page') {
        url.searchParams.delete(key);
      }
    }

    const forms = sourceForm ? [sourceForm] : [this.#getActiveFilterForm()].filter(Boolean);

    for (const form of forms) {
      if (!(form instanceof HTMLFormElement)) continue;

      const formData = new FormData(form);
      for (const [name, value] of formData.entries()) {
        if (!name.startsWith('filter.')) continue;
        url.searchParams.append(name, String(value));
      }
    }

    const sortSelect = this.querySelector('[data-collection-sort]');
    const sortValue =
      sortSelect instanceof HTMLSelectElement ? sortSelect.value : url.searchParams.get('sort_by');
    if (sortValue && sortValue !== 'manual') {
      url.searchParams.set('sort_by', sortValue);
    }

    return url;
  }

  /**
   * @returns {HTMLFormElement | null}
   */
  #getActiveFilterForm() {
    const form = this.querySelector('[data-collection-filters-form]');
    return form instanceof HTMLFormElement ? form : null;
  }

  /**
   * @param {string} sortValue
   */
  #applySort(sortValue) {
    const url = this.#buildUrlFromFilters(undefined);

    if (sortValue && sortValue !== 'manual') {
      url.searchParams.set('sort_by', sortValue);
    } else {
      url.searchParams.delete('sort_by');
    }

    url.searchParams.delete('page');
    this.#renderFromUrl(url, { focusTarget: '[data-collection-grid]' });
  }

  /**
   * @param {URL} url
   * @param {{ skipPush?: boolean, focusTarget?: string }} [options]
   */
  async #renderFromUrl(url, options = {}) {
    if (this._isRendering) return;

    const config = this.#parseConfig();
    if (!config?.sectionId) return;

    this._isRendering = true;
    const liveRegion = this.querySelector('[data-collection-live]');
    if (liveRegion) liveRegion.textContent = config.i18n?.loading || 'Loading';

    try {
      await sectionRenderer.renderSection(String(config.sectionId), {
        url,
        cache: false,
      });

      if (!options.skipPush) {
        history.pushState({}, '', toBrowserUrl(url).toString());
      }

      this.#bindEvents();
      this.#syncSortState();
      this.removeAttribute('data-catalog-hydrated');
      this._catalogHydrating = false;
      this._hydrateGeneration += 1;
      this.#scheduleIdleWork();

      if (liveRegion) liveRegion.textContent = config.i18n?.loaded || 'Content loaded';
      announce(config.i18n?.loaded || 'Content loaded', 'polite');

      if (options.focusTarget) {
        const focusEl = this.querySelector(options.focusTarget);
        if (focusEl instanceof HTMLElement) {
          const focused = focusFirstTabbable(focusEl);
          if (!focused) {
            focusEl.setAttribute('tabindex', '-1');
            safeFocus(focusEl);
          }

          const targetToScroll = focused || focusEl;
          const header = document.querySelector('.primary-header, header-component, .header');
          const headerOffset = header instanceof HTMLElement ? header.offsetHeight + 20 : 0;
          const elementPosition = targetToScroll.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - headerOffset;

          window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          });
        }
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } finally {
      this._isRendering = false;
    }
  }

  #syncSortState() {
    const url = new URL(window.location.href);
    const sortBy = url.searchParams.get('sort_by') || 'manual';

    for (const select of this.querySelectorAll('[data-collection-sort]')) {
      if (select instanceof HTMLSelectElement) {
        select.value = sortBy === 'best-selling' ? 'manual' : sortBy;
      }
    }

    for (const option of this.querySelectorAll('[data-collection-sort-option]')) {
      if (!(option instanceof HTMLButtonElement)) continue;
      const value = option.getAttribute('value') || 'manual';
      const isActive = value === sortBy || (value === 'manual' && !url.searchParams.get('sort_by'));
      option.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }
  }

  async #sendGtmView() {
    const config = this.#parseConfig();
    const products = this.#parseGtmProducts();
    if (!config || products.length === 0) return;

    const { sendCollectionView } = await import('@nulo/gtm');
    if (!this.isConnected) return;

    sendCollectionView({
      id: config.collectionId,
      title: config.collectionTitle,
      products,
    });
  }

  #isDesktopLayout() {
    return window.matchMedia('(min-width: 768px)').matches;
  }

  #getFilterPanel() {
    const panel = this.querySelector('[data-collection-filters-panel]');
    return panel instanceof HTMLElement ? panel : null;
  }

  #ensureFiltersInDrawer() {
    const slot = this.querySelector('[data-collection-filter-drawer-body]');
    if (!(slot instanceof HTMLElement)) return;
    if (slot.querySelector('[data-collection-filters-panel]')) return;
    const panel = this.#getFilterPanel();
    if (panel) slot.appendChild(panel);
  }

  #restoreFiltersToSidebar() {
    const sidebar = this.querySelector('[data-collection-sidebar]');
    if (!(sidebar instanceof HTMLElement)) return;
    if (sidebar.querySelector('[data-collection-filters-panel]')) return;
    const panel = this.querySelector('[data-collection-filter-drawer-body] [data-collection-filters-panel]');
    if (panel) sidebar.appendChild(panel);
  }

  #syncFilterTreeHost() {
    if (!this.#isDesktopLayout()) return;

    const drawer = this.querySelector('[data-collection-filter-drawer]');
    if (drawer instanceof HTMLDialogElement && drawer.open) {
      this.#closeFilterDrawer();
      return;
    }

    this.#restoreFiltersToSidebar();
  }

  #openFilterDrawer() {
    const drawer = this.querySelector('[data-collection-filter-drawer]');
    const trigger = this.querySelector('[data-collection-filter-open]');
    if (!(drawer instanceof HTMLDialogElement)) return;

    this.#ensureFiltersInDrawer();
    this.#hydrateParentFiltersCatalog();

    drawer.showModal();
    trigger?.setAttribute('aria-expanded', 'true');
    this._filterTrap?.();
    this._filterTrap = createFocusTrap(drawer, () => this.#closeFilterDrawer());
    const closeBtn = drawer.querySelector('[data-collection-filter-close]');
    if (closeBtn instanceof HTMLElement) safeFocus(closeBtn);
  }

  #closeFilterDrawer() {
    const drawer = this.querySelector('[data-collection-filter-drawer]');
    const trigger = this.querySelector('[data-collection-filter-open]');
    if (!(drawer instanceof HTMLDialogElement)) return;

    if (drawer.open) drawer.close();
    this.#restoreFiltersToSidebar();
    trigger?.setAttribute('aria-expanded', 'false');
    this._filterTrap?.();
    this._filterTrap = null;
    if (trigger instanceof HTMLElement) safeFocus(trigger);
  }

  #openSortDrawer() {
    const drawer = this.querySelector('[data-collection-sort-drawer]');
    const trigger = this.querySelector('[data-collection-sort-open]');
    if (!(drawer instanceof HTMLDialogElement)) return;

    drawer.showModal();
    trigger?.setAttribute('aria-expanded', 'true');
    this._sortTrap?.();
    this._sortTrap = createFocusTrap(drawer, () => this.#closeSortDrawer());
    const closeBtn = drawer.querySelector('[data-collection-sort-close]');
    if (closeBtn instanceof HTMLElement) safeFocus(closeBtn);
  }

  #closeSortDrawer() {
    const drawer = this.querySelector('[data-collection-sort-drawer]');
    const trigger = this.querySelector('[data-collection-sort-open]');
    if (!(drawer instanceof HTMLDialogElement)) return;

    if (drawer.open) drawer.close();
    trigger?.setAttribute('aria-expanded', 'false');
    this._sortTrap?.();
    this._sortTrap = null;
    if (trigger instanceof HTMLElement) safeFocus(trigger);
  }
}

if (!customElements.get('collection-main')) {
  customElements.define('collection-main', CollectionMain);
}
